import { type FC, useEffect, useCallback, useState } from 'react';
import { graphql, useLazyLoadQuery, useFragment, useRelayEnvironment } from 'react-relay';
import classnames from 'classnames';
import dibsCss from 'dibs-css';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { SeoLink, ALWAYS_LINKABLE_LINK } from 'dibs-elements/exports/SeoLink.tsx';
import { ScrollCarousel } from 'dibs-scroll-carousel/exports/ScrollCarousel.ts';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';
import ChevronRight from 'dibs-icons/exports/ChevronRight.ts';
import { getUserEventItem, trackUserEventItemAddToFavorites } from 'dibs-tracking';
import { BuyerProductImageOnly } from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';
import { useHomePageContext } from '../HomePageContext.tsx';
import { trackModule } from '../helpers/moduleLocationTracking.ts';
import {
    trackNewArrivalsViewMoreClick,
    trackNewArrivalsArrowClick,
    trackNewArrivalsInitialDisplay,
    trackNewArrivalsItemClick,
    trackNewArrivalsItemImpression,
} from '../helpers/hpRvNewArrivalsTracking.ts';
import ItemHeartWrapper from 'dibs-portfolio-heart/exports/ItemHeartWrapper.js';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';
import { getBuyerId } from 'dibs-cookie-jar';

import { type HpNewArrivalsCarouselQuery } from './__generated__/HpNewArrivalsCarouselQuery.graphql.ts';
import { type HpNewArrivalsCarousel_item$key } from './__generated__/HpNewArrivalsCarousel_item.graphql.ts';
import { type HpNewArrivalsCarousel_viewer$key } from './__generated__/HpNewArrivalsCarousel_viewer.graphql.ts';

const ITEMS_PER_PAGE = 6;
const ITEMS_PER_PAGE_TABLET = 5;
const ITEMS_PER_PAGE_MOBILE = 2.5;
const TOTAL_ITEMS = 12;
const TRACKING_TITLE = 'New Arrivals';
const NEW_ARRIVALS_PATH = '/new-arrivals/?sort=newest';

const newArrivalsQuery = graphql`
    query HpNewArrivalsCarouselQuery($first: Int!, $uriRef: String!, $userId: String!) {
        viewer {
            ...HpNewArrivalsCarousel_viewer @arguments(userId: $userId)
            itemSearch(first: $first, uriRef: $uriRef) {
                edges {
                    node {
                        item {
                            serviceId
                            title
                            browseUrl
                            categoryCode
                            contemporaryTrackingString
                            seller {
                                serviceId
                            }
                            ...HpNewArrivalsCarousel_item @arguments(userId: $userId)
                        }
                    }
                }
            }
        }
    }
`;

const viewerFragment = graphql`
    fragment HpNewArrivalsCarousel_viewer on Viewer
    @argumentDefinitions(userId: { type: "String", defaultValue: "" }) {
        ...ItemHeartWrapper_viewer @arguments(userIds: [$userId], loadPortfolioData: true)
    }
`;

const itemFragment = graphql`
    fragment HpNewArrivalsCarousel_item on Item
    @argumentDefinitions(userId: { type: "String", defaultValue: "" }) {
        serviceId
        title
        firstPhotoWebPath(size: small)
        ecommerceTrackingParams
        browseUrl
        categoryCode
        contemporaryTrackingString
        seller {
            serviceId
        }
        ...BuyerProductImageOnly_item
        ...ItemHeartWrapper_item @arguments(userId: $userId, loadPortfolioData: true)
    }
`;

type ItemTileProps = {
    itemRef: HpNewArrivalsCarousel_item$key;
    viewerRef: HpNewArrivalsCarousel_viewer$key;
    index: number;
    userId: string;
};

const ItemTile: FC<ItemTileProps> = ({ itemRef, viewerRef, index, userId }) => {
    const { isMobile } = useHomePageContext();
    const environment = useRelayEnvironment();
    const item = useFragment(itemFragment, itemRef);
    const viewer = useFragment(viewerFragment, viewerRef);
    const [isLoginFlowInProgress, setIsLoginFlowInProgress] = useState(false);

    const trackingItem = {
        itemId: item.serviceId || '',
        itemTitle: item.title || '',
        brand: item.seller?.serviceId,
        category: item.browseUrl,
        categoryCode: item.categoryCode,
        contemporaryTrackingString: item.contemporaryTrackingString,
    };

    const handleClick = (): void => {
        trackNewArrivalsItemClick({ item: trackingItem, index });
        trackModule(TRACKING_TITLE);
    };

    const handleContentLoaded = (): void => {
        trackNewArrivalsItemImpression({ item: trackingItem, index });
    };

    const heartAuthModalShow = useCallback(() => {
        authModalLoader
            ?.show({
                flow: authModalLoader?.constants?.SAVE_ITEM_FLOW,
            })
            .then(() => {
                setIsLoginFlowInProgress(true);
            });
    }, []);

    const handleFavorited = useCallback(() => {
        const userEventItem = getUserEventItem({
            id: item.serviceId,
            amount: item.ecommerceTrackingParams?.price,
            convertedAmounts: item.ecommerceTrackingParams?.convertedAmounts,
        });
        if (userEventItem) {
            trackUserEventItemAddToFavorites(environment, [userEventItem]);
        }
    }, [environment, item.serviceId, item.ecommerceTrackingParams]);

    return (
        <BuyerProductImageOnly
            item={item}
            dataTn="new-arrivals-tile"
            imageBackgroundColor="moonStone"
            index={index}
            itemsPerRow={0}
            onClick={handleClick}
            onContentLoaded={handleContentLoaded}
            size="small"
            srcSet={{ sizes: getSrcsetString(item.firstPhotoWebPath || '') }}
            favoritesComponent={
                <div
                    className={classnames(
                        dibsCss.absolute,
                        dibsCss.top0,
                        dibsCss.right0,
                        dibsCss.mtXxsmall,
                        dibsCss.prXxsmall
                    )}
                >
                    <ItemHeartWrapper
                        theme={isMobile ? 'circle-frosted-small' : 'circle-frosted-medium'}
                        itemId={item.serviceId}
                        userId={userId}
                        viewer={viewer}
                        item={item}
                        authModalLoader={authModalLoader}
                        authModalShow={heartAuthModalShow}
                        onFavorited={handleFavorited}
                        isLoginFlowInProgress={isLoginFlowInProgress}
                    />
                </div>
            }
        />
    );
};

export const HpNewArrivalsCarousel: FC = () => {
    const { isMobile, isTablet } = useHomePageContext();
    // Read the cookie during render: deferring it to an effect changes the query variables after
    // mount, which re-suspends this boundary and swaps the rendered carousel back to the shimmer.
    const userId = typeof document !== 'undefined' ? getBuyerId(document.cookie) : '';

    const data = useLazyLoadQuery<HpNewArrivalsCarouselQuery>(newArrivalsQuery, {
        first: TOTAL_ITEMS,
        uriRef: NEW_ARRIVALS_PATH,
        userId,
    });

    const items = data.viewer?.itemSearch?.edges || [];

    useEffect(() => {
        if (items.length) {
            trackNewArrivalsInitialDisplay({ itemsReturned: items.length });
        }
    }, [items.length]);

    if (!items.length) {
        return null;
    }

    const handleViewMoreClick = (): void => {
        trackNewArrivalsViewMoreClick();
        trackModule(TRACKING_TITLE);
    };

    const handleArrowClick = ({ direction }: { direction: string }): void => {
        trackNewArrivalsArrowClick({ direction });
    };

    const title = (
        <FormattedMessage id="HpNewArrivalsCarousel.title" defaultMessage="See What's New" />
    );

    let itemsPerPage = ITEMS_PER_PAGE;
    if (isMobile) {
        itemsPerPage = ITEMS_PER_PAGE_MOBILE;
    } else if (isTablet) {
        itemsPerPage = ITEMS_PER_PAGE_TABLET;
    }

    return (
        <div
            className={classnames(dibsCss.pyLarge, {
                [dibsCss.pbMedium]: isMobile,
                [dibsCss.ptMedium]: isMobile,
                'primary-viewport-width': isMobile,
            })}
        >
            <div
                className={classnames(dibsCss.flex, dibsCss.gapLarge, dibsCss.wFull, {
                    [dibsCss.itemsCenter]: !isMobile,
                    [dibsCss.flexCol]: isMobile,
                    [dibsCss.gapSmall]: isMobile || isTablet,
                })}
            >
                <div
                    className={classnames(dibsCss.textNoir, dibsCss.flexShrink0, {
                        [dibsCss.w200px]: !isMobile && !isTablet,
                        [dibsCss.wFull]: isMobile,
                        [dibsCss.plSmall]: isMobile,
                    })}
                    style={isTablet ? { width: 160 } : undefined}
                    data-tn="new-arrivals-header"
                >
                    <HeadingLevel>
                        {Heading => (
                            <Heading
                                className={classnames(dibsCss.sassyFontHeaderLarge, dibsCss.mt0, {
                                    [dibsCss.sassyFontHeaderMedium]: isMobile || isTablet,
                                    [dibsCss.mbXsmall]: !isMobile,
                                    [dibsCss.mb0]: isMobile,
                                })}
                            >
                                {isMobile ? (
                                    <SeoLink
                                        buyerLinkType="standardLinkNoUnderline"
                                        variant={ALWAYS_LINKABLE_LINK}
                                        linkData={{ path: NEW_ARRIVALS_PATH, isLinkable: false }}
                                        onClick={handleViewMoreClick}
                                        dataTn="new-arrivals-link"
                                    >
                                        <span
                                            className={classnames(
                                                dibsCss.inlineFlex,
                                                dibsCss.itemsCenter,
                                                dibsCss.gapXxsmall
                                            )}
                                        >
                                            {title}
                                            <ChevronRight variation="24" />
                                        </span>
                                    </SeoLink>
                                ) : (
                                    title
                                )}
                            </Heading>
                        )}
                    </HeadingLevel>
                    {!isMobile && (
                        <SeoLink
                            className={dibsCss.sassyFontBody}
                            variant={ALWAYS_LINKABLE_LINK}
                            linkData={{ path: NEW_ARRIVALS_PATH, isLinkable: false }}
                            onClick={handleViewMoreClick}
                            dataTn="new-arrivals-link"
                        >
                            <FormattedMessage
                                id="HpNewArrivalsCarousel.viewAllCta"
                                defaultMessage="All New Arrivals"
                            />
                        </SeoLink>
                    )}
                </div>
                <div className={classnames(dibsCss.flex1, dibsCss.minW0, dibsCss.overflowHidden)}>
                    <ScrollCarousel
                        animation="slide"
                        enableScroll
                        itemsToShow={itemsPerPage}
                        dataTn="new-arrivals-carousel"
                        noGap
                        gapSize="small"
                        {...(isMobile
                            ? {
                                  stepSize: 1,
                                  scrollPadding: 'small',
                              }
                            : {
                                  stepSize: itemsPerPage,
                                  isInfinite: true,
                                  showArrows: true,
                                  onArrowClick: handleArrowClick,
                              })}
                    >
                        {items.map((edge, index) => {
                            const item = edge?.node?.item;
                            if (!item || !data.viewer) return null;
                            return (
                                <ItemTile
                                    key={item.serviceId}
                                    itemRef={item}
                                    viewerRef={data.viewer}
                                    index={index}
                                    userId={userId}
                                />
                            );
                        })}
                    </ScrollCarousel>
                </div>
            </div>
        </div>
    );
};
